import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, ChevronRight, Layers3, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import services, { principles, workflow } from "../data/services";
import Seo from "../seo/Seo";
import pageMeta from "../data/pageMeta";

import "../styles/services.css";

const reveal = {
  hidden: {
    opacity: 0,
    y: 28,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const heroStats = [
  {
    value: "5",
    label: "Core capabilities",
  },
  {
    value: "1",
    label: "Connected system",
  },
  {
    value: "360°",
    label: "Execution coverage",
  },
];

const heroHighlights = ["Strategy-led", "Experience-first", "Built to scale"];

const proofCards = [
  {
    icon: Sparkles,
    title: "Premium from the first click",
    text: "A cleaner hierarchy, stronger pacing and sharper content flow make the whole page feel intentional.",
  },
  {
    icon: Layers3,
    title: "Everything connected",
    text: "The copy, imagery and motion all work together as one system instead of a stack of separate blocks.",
  },
  {
    icon: ArrowRight,
    title: "Designed to convert",
    text: "The UX nudges users toward the right action without making the journey feel heavy or noisy.",
  },
];

function ServicesPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [activeId, setActiveId] = useState(services[0].id);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(mouseY, {
    stiffness: 120,
    damping: 18,
  });

  const rotateY = useSpring(mouseX, {
    stiffness: 120,
    damping: 18,
  });

  const translateX = useTransform(mouseX, [-12, 12], [-14, 14]);
  const translateY = useTransform(mouseY, [-12, 12], [-14, 14]);

  const activeService = useMemo(
    () => services.find((service) => service.id === activeId) || services[0],
    [activeId]
  );

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;

    const move = (event) => {
      const rect = hero.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      mouseX.set((x - 0.5) * 16);
      mouseY.set(-(y - 0.5) * 16);
    };

    const leave = () => {
      mouseX.set(0);
      mouseY.set(0);
    };

    hero.addEventListener("mousemove", move);
    hero.addEventListener("mouseleave", leave);

    return () => {
      hero.removeEventListener("mousemove", move);
      hero.removeEventListener("mouseleave", leave);
    };
  }, [mouseX, mouseY]);

  const scrollToService = (id) => {
    setActiveId(id);
    document.getElementById(`service-${id}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <>
      <Seo title={pageMeta.services.title} description={pageMeta.services.description} />

      <main className="services-page">
        <section ref={heroRef} className="services-hero">
          <div className="services-hero__grid" />
          <div className="services-hero__glow services-hero__glow--left" />
          <div className="services-hero__glow services-hero__glow--right" />

          <div className="container services-hero__layout">
            <motion.div
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="services-hero__copy"
            >
              <span className="section-chip">Services</span>
              <p className="services-hero__eyebrow">A connected growth system</p>

              <h1 className="services-hero__title">
                Strategy, design and technology shaped like one premium product.
              </h1>

              <p className="services-hero__lead">
                We do not stitch channels together. We build a complete operating system for
                growth, where every touchpoint feels intentional, modern and conversion-ready.
              </p>

              <div className="services-hero__pills" aria-label="Service highlights">
                {heroHighlights.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>

              <div className="services-hero__actions">
                <button type="button" className="button-primary" onClick={() => scrollToService(services[0].id)}>
                  Explore services
                  <ArrowDown size={18} />
                </button>

                <button type="button" className="button-secondary" onClick={() => navigate("/contact")}>
                  Start a project
                  <ArrowRight size={18} />
                </button>
              </div>

              <div className="services-hero__stats">
                {heroStats.map((stat) => (
                  <div key={stat.label} className="hero-stat glass-card">
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.aside
              className="services-hero__showcase glass-panel"
              style={{
                rotateX,
                rotateY,
                transformPerspective: 1400,
              }}
            >
              <motion.div
                className="services-hero__showcase-inner"
                style={{
                  x: translateX,
                  y: translateY,
                }}
              >
                <div className="services-hero__showcase-top">
                  <div>
                    <span className="panel-label">Live service deck</span>
                    <h3>{activeService.title}</h3>
                  </div>

                  <div className="services-hero__meter">
                    <strong>{activeService.metric.value}</strong>
                    <span>{activeService.metric.label}</span>
                  </div>
                </div>

                <div className="services-hero__media-shell">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeService.id}
                      src={activeService.image}
                      alt={activeService.title}
                      initial={{
                        opacity: 0,
                        scale: 1.04,
                        y: 16,
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.98,
                        y: -10,
                      }}
                      transition={{
                        duration: 0.45,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="services-hero__service-image"
                    />
                  </AnimatePresence>

                  <div className="services-hero__media-badge">Premium execution stack</div>
                </div>

                <div className="services-hero__showcase-bottom">
                  <div className="services-hero__copycard">
                    <p>{activeService.description}</p>

                    <div className="services-hero__deliverables">
                      {activeService.deliverables.map((item) => (
                        <div key={item} className="services-hero__deliverable">
                          <span />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="services-hero__selector" aria-label="Service selector">
                    {services.map((service) => {
                      const Icon = service.icon;

                      return (
                        <button
                          key={service.id}
                          type="button"
                          className={activeId === service.id ? "services-hero__selector-item active" : "services-hero__selector-item"}
                          onClick={() => setActiveId(service.id)}
                        >
                          <span className="services-hero__selector-icon">
                            <Icon size={16} />
                          </span>

                          <span className="services-hero__selector-copy">
                            <strong>{service.navigation}</strong>
                            <span>{service.metric.label}</span>
                          </span>

                          <ChevronRight size={16} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </motion.aside>
          </div>

          <motion.button
            type="button"
            className="scroll-indicator"
            aria-label="Scroll to service details"
            animate={{
              y: [0, 6, 0],
            }}
            transition={{
              duration: 2.2,
              repeat: Infinity,
            }}
            onClick={() => scrollToService(services[0].id)}
          >
            <ArrowDown size={18} />
          </motion.button>
        </section>

        <section className="services-proof">
          <div className="container services-proof__grid">
            {proofCards.map((item) => {
              const Icon = item.icon;

              return (
                <motion.article
                  key={item.title}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                    amount: 0.3,
                  }}
                  className="proof-card glass-card"
                >
                  <div className="proof-card__icon">
                    <Icon size={18} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </motion.article>
              );
            })}
          </div>
        </section>

        <section className="services-matrix">
          <div className="container">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.3,
              }}
              className="section-heading section-heading--center"
            >
              <span className="section-chip">What we do</span>
              <h2>One connected service stack.</h2>
              <p>
                Each capability is useful on its own, but the real value comes from how
                naturally they work together as one premium experience.
              </p>
            </motion.div>

            <div className="services-matrix__grid">
              {services.map((service, index) => {
                const Icon = service.icon;
                const isWide = index === 0 || index === 3;

                return (
                  <motion.article
                    key={service.id}
                    id={`service-${service.id}`}
                    variants={reveal}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                      once: true,
                      amount: 0.22,
                    }}
                    className={isWide ? "matrix-card matrix-card--wide glass-card" : "matrix-card glass-card"}
                  >
                    <div className="matrix-card__top">
                      <div className="matrix-card__icon">
                        <Icon size={20} />
                      </div>
                      <span className="matrix-card__eyebrow">{service.eyebrow}</span>
                    </div>

                    <div className="matrix-card__content">
                      <h3>{service.title}</h3>
                      <p>{service.headline}</p>

                      <div className="matrix-card__list">
                        {service.deliverables.map((item) => (
                          <div key={item} className="matrix-card__item">
                            <span />
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <img className="matrix-card__media" src={service.image} alt={service.title} />

                    <div className="matrix-card__footer">
                      <div className="matrix-card__metric">
                        <strong>{service.metric.value}</strong>
                        <span>{service.metric.label}</span>
                      </div>

                      <button type="button" onClick={() => setActiveId(service.id)}>
                        Preview live
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="services-process">
          <div className="container">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.3,
              }}
              className="section-heading"
            >
              <span className="section-chip">How we work</span>
              <h2>A simple path from insight to launch.</h2>
              <p>
                The process stays calm and clear so every phase feels premium, collaborative
                and easy to trust.
              </p>
            </motion.div>

            <div className="process-timeline">
              {workflow.map((step) => (
                <motion.div
                  key={step.step}
                  initial={{
                    opacity: 0,
                    y: 24,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.6,
                  }}
                  className="process-step glass-card"
                >
                  <span className="process-step__index">{step.step}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="services-principles">
          <div className="container">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.3,
              }}
              className="section-heading section-heading--center"
            >
              <span className="section-chip">Our principles</span>
              <h2>Every engagement follows the same standards.</h2>
              <p>Strategy-led, design-aware and measured with business outcomes in mind.</p>
            </motion.div>

            <div className="principles-grid">
              {principles.map((principle, index) => (
                <motion.div
                  key={principle.title}
                  initial={{
                    opacity: 0,
                    y: 24,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.08,
                    duration: 0.6,
                  }}
                  className="principle-card glass-card"
                >
                  <span className="principle-card__number">{String(index + 1).padStart(2, "0")}</span>
                  <h3>{principle.title}</h3>
                  <p>{principle.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="services-cta">
          <div className="container">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.3,
              }}
              className="cta-panel glass-panel"
            >
              <div className="cta-panel__copy">
                <span className="section-chip">EkritFly</span>
                <h2>One partner for strategy, design and growth.</h2>
                <p>
                  If you want a premium system that feels connected from first touch to final
                  conversion, we can build it with you.
                </p>
              </div>

              <div className="cta-panel__actions">
                <button type="button" className="button-primary" onClick={() => navigate("/contact")}>
                  Start your project
                  <ArrowRight size={18} />
                </button>

                <button type="button" className="button-secondary" onClick={() => navigate("/industries")}>
                  Explore industries
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}

export default ServicesPage;
